<script setup>
import HiddenByTemplateActionRow from "@/components/actions/partials/HiddenByTemplateActionRow.vue";
import CustomMultiselect from "@/components/global-themed/CustomMultiselect.vue";
import GroupedActionPanel from "@/components/actions/partials/GroupedActionPanel.vue";
import ActionTime from "@/components/actions/partials/ActionTime.vue";
import {computed, ref, watch} from "vue";
import {useQuery} from "@tanstack/vue-query";
import axios from "axios";
import FadeTransition from "@/components/transitions/FadeTransition.vue";
import ActionCategoryIcon from "@/components/actions/partials/ActionCategoryIcon.vue";
import PaginationButtons from "@/components/global-themed/PaginationButtons.vue";
import {usePagination} from '@/composables/usePagination';
import GroupedActionPanelTitle from "@/components/actions/partials/GroupedActionPanelTitle.vue";

const props = defineProps({
  category: {
    type: String,
    required: true,
  },
  subCategories: {
    type: Array,
    required: true,
  },
});

const selectedSubCategory = ref();

const {currentPages, perPage, getPaginationCount, handlePageChange} = usePagination();

const currentPage = computed({
  get: () => currentPages.value[props.category] || 1,
  set: (page) => handlePageChange(props.category, page)
});

const {data: hiddenActionTemplateData, isLoading} = useQuery({
  queryKey: ['hidden-actions-by-template', props.category],
  queryFn: async () => {
    const {data} = await axios.get(`/ajax/v2/actions/hidden/by-template?actionCategory=${props.category}`);
    return data.data;
  },
});

const hiddenActionTemplates = computed(() => {
  if (!hiddenActionTemplateData.value) {
    return [];
  }

  return hiddenActionTemplateData.value.filter(templateAction => {
    if (!selectedSubCategory.value) {
      return true;
    }

    return templateAction.sub_category === selectedSubCategory.value;
  })
});

const totalHiddenActionTemplates = computed(() => hiddenActionTemplates.value.length);

const filteredHiddenActionTemplates = computed(() => {
  const totalPages = Math.ceil(hiddenActionTemplates.value.length / perPage);

  if (currentPage.value > totalPages && hiddenActionTemplates.value.length > 0) {
    handlePageChange(props.category, 1);
  }

  const startIndex = (currentPage.value - 1) * perPage;
  const endIndex = startIndex + perPage;

  return hiddenActionTemplates.value.slice(startIndex, endIndex);
});

const totalTime = computed(() => {
  if (!hiddenActionTemplates.value) {
    return 0;
  }

  return hiddenActionTemplates.value
      .map(action => action.grouped_action_template_time)
      .reduce((total, time) => total + time, 0);
});

const pages = computed(() => {
  if (!hiddenActionTemplates.value) {
    return 0;
  }
  return Math.ceil(totalHiddenActionTemplates.value / perPage);
});

// Reset pagination when filter changes
watch(selectedSubCategory, () => {
  handlePageChange(props.category, 1);
});
</script>

<template>
  <GroupedActionPanel>
    <template #title>
      <div class="tw-flex tw-items-center tw-gap-6">
        <ActionCategoryIcon :category="category" />

        <GroupedActionPanelTitle :title="category" />
      </div>
    </template>

    <template #action-time>
      <FadeTransition>
        <ActionTime
          v-if="!isLoading"
          :minutes="totalTime"
          class="tw-font-semibold"
        />
      </FadeTransition>
    </template>

    <template #toolbar>
      <CustomMultiselect
        v-if="subCategories && !!subCategories"
        id="selectedSubCategory"
        v-model="selectedSubCategory"
        class="tw-text-left tw-w-[325px] tw-h-12.5 tw-relative"
        :close-on-select="true"
        :options="subCategories"
        :searchable="true"
        :can-deselect="true"
        placeholder="Select Subcategory"
      >
        <template #icon>
          <svg-vue
            icon="icons/filter-funnel"
            class="tw-w-6 tw-h-6 tw-text-grey"
          />
        </template>
      </CustomMultiselect>
    </template>

    <template #actions>
      <HiddenByTemplateActionRow
        v-for="hiddenActionTemplate in filteredHiddenActionTemplates"
        :key="hiddenActionTemplate.action_template_id"
        :template="hiddenActionTemplate"
      />
    </template>

    <template #pagination>
      <div
        v-if="totalHiddenActionTemplates > perPage"
        class="tw-flex tw-items-center tw-justify-between tw-w-full"
      >
        <div class="tw-flex tw-items-center tw-justify-center tw-w-full">
          <PaginationButtons
            :last-page="pages"
            :current-page="currentPage"
            @change="newPage => currentPage = newPage"
          />
        </div>

        <span class="tw-text-grey-dark tw-font-bold tw-text-base tw-ml-auto tw-whitespace-nowrap">
          {{ getPaginationCount(props.category, totalHiddenActionTemplates) }} Actions
        </span>
      </div>
    </template>
  </GroupedActionPanel>
</template>
